import { computed, inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { tap, catchError, map, finalize, shareReplay } from 'rxjs/operators';
import { User, UserShop } from '@app/types';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@app/auth';
import { LocalDbService } from './local-db.service';

export interface LoginCredentials {
	email: string;
	password: string;
}

@Service()
export class UserService {
	readonly #localDb = inject(LocalDbService);
	readonly #authService = inject(AuthService);
	readonly #httpClient = inject(HttpClient);

	readonly #currentShopSub$ = new BehaviorSubject<UserShop | null>(null);
	readonly #loadingUserSub$ = new BehaviorSubject(false);
	loadingUser$ = this.#loadingUserSub$.asObservable().pipe(shareReplay(1));
	currentShop$ = this.#currentShopSub$.asObservable().pipe(shareReplay(1));
	currentShop = toSignal(this.currentShop$);

	private readonly API_URL = `${environment.apiUrl}/users`;

	/** EVENTS */
	loggedUserEvt$ = new Subject<User>();

	user = toSignal(
		merge(this.#authService.isAuthenticated() ? this.#getMe() : of(null), this.loggedUserEvt$).pipe(
			tap(async (user) => {
				if (user) {
					const stringUserId = `user_${user.id}`;
		
					const collections = await this.#localDb.getCollections();
					const doc = await collections.user_settings
						.findOne(stringUserId)
						.exec();

					const id_shop_saved = doc?.currentShopId;
					const shop_to_select =
						user.shops?.find((s) => s.id === id_shop_saved) ?? user.shops?.[0] ?? null;
					this.#currentShopSub$.next(shop_to_select);
				}
			}),
			shareReplay(1),
		),
	);
	userShops = computed(() => this.user()?.shops ?? []);

	async setCurrentShop(currentShop: UserShop): Promise<void> {
		this.#currentShopSub$.next(currentShop);
		const user = this.user();
		if (currentShop && user) {
			const stringUserId = `user_${user.id}`;
			const collections = await this.#localDb.getCollections();
			await collections.user_settings.upsert({
				userId: stringUserId,
				currentShopId: currentShop.id,
				updatedAt: Date.now()
			});
		}
	}

	login(credentials: LoginCredentials): Observable<User> {
		return this.#httpClient.post<{ user: User }>(`${this.API_URL}/login`, credentials).pipe(
			map(({ user }) => user),
			tap((user) => {
				this.loggedUserEvt$.next(user);
			}),
			catchError((error) => {
				console.error('Login failed:', error);
				throw error;
			}),
		);
	}

	/**
	 * Fetch data for logged user
	 */
	#getMe(): Observable<User> {
		this.#loadingUserSub$.next(true);
		return this.#httpClient
			.get<User>(`${this.API_URL}/me`)
			.pipe(finalize(() => this.#loadingUserSub$.next(false)));
	}
}
